<template>
  <transition name="share-modal-fade">
    <div v-if="modelValue" class="share-modal-mask" @click.self="close">
      <div class="share-modal-panel">
        <!-- 选择频道 -->
        <template v-if="!isSuccess">
          <div class="share-modal-title">
            {{ t("shareModal.selectChannelTitle") }}
          </div>
          <div class="share-modal-content">
            <div class="share-modal-row">
              <label class="share-modal-label">{{
                t("shareModal.channelLabel")
              }}</label>
              <div class="share-modal-select-wrap">
                <select
                  v-model="selectedChannelId"
                  class="share-modal-select"
                  :disabled="shareStore.channelLoading"
                >
                  <option value="">
                    {{ t("shareModal.channelPlaceholder") }}
                  </option>
                  <option
                    v-for="ch in channelOptions"
                    :key="ch.id"
                    :value="String(ch.id)"
                  >
                    {{ t(`shareModal.${ch.name}`) }}
                  </option>
                </select>
              </div>
            </div>
            <p v-if="shareStore.channelError" class="share-modal-error">
              {{ shareStore.channelError }}
            </p>
          </div>
          <div class="share-modal-actions">
            <button
              class="share-modal-btn share-modal-btn-cancel"
              @click="onCancelShare"
            >
              {{ t("common.cancel") }}
            </button>
            <button
              class="share-modal-btn share-modal-btn-confirm"
              :disabled="!selectedChannelId || shareStore.submitLoading"
              @click="onConfirmShare"
            >
              {{
                shareStore.submitLoading
                  ? t("common.loading")
                  : t("common.confirm")
              }}
            </button>
          </div>
        </template>

        <!-- 分享成功 -->
        <template v-else>
          <div class="share-modal-title share-modal-title-success">
            {{ t("shareModal.shareSuccess") }}
          </div>
          <div class="share-modal-actions share-modal-actions-success">
            <button
              class="share-modal-btn share-modal-btn-secondary"
              @click="onContinueBrowse"
            >
              {{ t("shareModal.continueBrowse") }}
            </button>
            <button
              v-if="runInsdk"
              class="share-modal-btn share-modal-btn-primary"
              @click="onGoToGame"
            >
              {{ t("shareModal.goToGame") }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useShareStore } from "@/stores/share";
import { useToastStore } from "@/stores";
import type { ShareThreadInfo } from "@/stores/share";
import sdkBridge from "@/utils/sdkBridge";
import { clickButton, pageType } from "@/utils/point";

const { t } = useI18n();
const shareStore = useShareStore();
const toastStore = useToastStore();

const props = defineProps<{
  modelValue: boolean;
  /** 帖子信息，用于提交分享；无则不能提交 */
  threadInfo?: ShareThreadInfo | null;
}>();
const emits = defineEmits(["update:modelValue"]);

const isSuccess = ref(false);
const selectedChannelId = ref("");

const channelOptions = computed(() => shareStore.channelList);
const runInsdk = computed(() => sdkBridge.runInsdk());

watch(
  () => props.modelValue,
  async (show) => {
    if (show) {
      isSuccess.value = false;
      selectedChannelId.value = "";
      await shareStore.getChannels();
      if (shareStore.channelList.length > 0) {
        selectedChannelId.value = String(shareStore.channelList[0].id);
      }
    }
  }
);

const close = () => {
  emits("update:modelValue", false);
};

const shareEventValue = (extra?: Record<string, unknown>) =>
  JSON.stringify({
    tid: props.threadInfo?.tid,
    ...extra,
  });

const onCancelShare = () => {
  clickButton({
    eventType: pageType.POST_DETAIL,
    eventReason: "分享弹窗取消",
    eventValue: shareEventValue({
      channel_id: selectedChannelId.value || undefined,
    }),
  });
  close();
};

const onConfirmShare = async () => {
  if (!selectedChannelId.value || !props.threadInfo) return;
  const result = await shareStore.submitShare(
    selectedChannelId.value,
    props.threadInfo
  );
  if (result.success) {
    clickButton({
      eventType: pageType.POST_DETAIL,
      eventReason: "分享到游戏提交成功",
      eventValue: shareEventValue({
        channel_id: selectedChannelId.value,
      }),
    });
    isSuccess.value = true;
  } else {
    toastStore.showErrorCodeToast(result.code, t);
  }
};

const onContinueBrowse = () => {
  clickButton({
    eventType: pageType.POST_DETAIL,
    eventReason: "分享成功继续浏览",
    eventValue: shareEventValue(),
  });
  close();
};

const onGoToGame = () => {
  clickButton({
    eventType: pageType.POST_DETAIL,
    eventReason: "分享成功前往游戏",
    eventValue: shareEventValue(),
  });
  sdkBridge.callSdkMethod("invokeGameAction", {
    action: "openChat",
    data: {},
    version: "1.0",
  });
  close();
};
</script>

<style scoped lang="scss">
.share-modal-fade-enter-active,
.share-modal-fade-leave-active {
  transition: opacity 0.2s;
}
.share-modal-fade-enter-from,
.share-modal-fade-leave-to {
  opacity: 0;
}

.share-modal-mask {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
}

.share-modal-panel {
  width: 90vw;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  padding: 28px 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: share-modal-in 0.25s;
}

@keyframes share-modal-in {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.share-modal-title {
  font-size: 20px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 24px;
  color: #222;
}

.share-modal-title-success {
  margin-bottom: 28px;
}

.share-modal-content {
  margin-bottom: 24px;
}

.share-modal-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.share-modal-label {
  font-size: 15px;
  color: #666;
  min-width: 72px;
}

.share-modal-select-wrap {
  flex: 1;
}

.share-modal-select {
  width: 100%;
  font-size: 15px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #222;
  cursor: pointer;
}

.share-modal-select:focus {
  outline: none;
  border-color: #4a90e2;
}

.share-modal-select:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.share-modal-error {
  margin: 8px 0 0;
  font-size: 13px;
  color: #e74c3c;
}

.share-modal-actions {
  display: flex;
  gap: 12px;
}

.share-modal-actions-success {
  flex-direction: column;
}

.share-modal-btn {
  flex: 1;
  height: 44px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  padding: 5px 0;
}

.share-modal-btn-cancel {
  background: #f5f5f5;
  color: #666;
}

.share-modal-btn-cancel:hover {
  background: #e8e8e8;
}

.share-modal-btn-confirm {
  background: #4a90e2;
  color: #fff;
}

.share-modal-btn-confirm:hover:not(:disabled) {
  background: #3a7bc8;
}

.share-modal-btn-confirm:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.share-modal-btn-secondary {
  background: #f5f5f5;
  color: #666;
}

.share-modal-btn-secondary:hover {
  background: #e8e8e8;
}

.share-modal-btn-primary {
  background: #4a90e2;
  color: #fff;
}

.share-modal-btn-primary:hover {
  background: #3a7bc8;
}
</style>
